import React, { useState, useEffect, useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { useTitle } from "../../../../hooks/useTitle";
import { withAuth } from "../../../../components/HOC/withAuth";
import CardContainer from "../../../../components/CustomUI/CardContainer";
import { SectionSpinner, SectionEmpty, salesMonthLabelTr } from "../shared/utils";
import { RP_PRODUCTION_TEALS } from "../shared/constants";
import { useDeliveriesReport } from "./useDeliveriesReport";
import { useDeliveriesByModel } from "./useDeliveriesByModel";
import DeliveriesReportShell from "./DeliveriesReportShell";

const DeliveriesByModelReport = () => {
  useTitle("Rovion MES - Model Bazlı Teslimat");
  const {
    years,
    year,
    handleYearChange,
    deliveriesData,
    loading: deliveriesLoading,
    toasts,
    dismissToast,
  } = useDeliveriesReport();

  const [month, setMonth] = useState("all");
  const { modelRows, loading: modelLoading } = useDeliveriesByModel(year, month);

  useEffect(() => {
    setMonth("all");
  }, [year]);

  const monthOptions = useMemo(() => {
    const opts = [{ value: "all", label: "Tüm Yıl" }];
    (deliveriesData?.monthlyDeliveries || []).forEach((m, i) => {
      const hasData =
        (m.deviceCount || 0) > 0 ||
        (m.models || []).some((mod) => (mod.count || 0) > 0);
      if (hasData) {
        opts.push({
          value: String(i),
          label: salesMonthLabelTr(i, m.year || year),
        });
      }
    });
    return opts;
  }, [deliveriesData, year]);

  const loading = deliveriesLoading || modelLoading;

  const periodLabel =
    month === "all"
      ? String(year)
      : monthOptions.find((o) => o.value === month)?.label || String(year);

  const content = () => {
    if (loading && !modelRows.length) return <SectionSpinner />;
    if (!modelRows.length)
      return (
        <SectionEmpty msg="Seçilen dönemde model bazlı teslimat verisi bulunamadı." />
      );

    return (
      <CardContainer title={`Model Bazlı Teslimat Adedi (${periodLabel})`}>
        <ReactApexChart
          type="bar"
          series={[{ name: "Teslimat Adedi", data: modelRows.map((m) => m.count) }]}
          options={{
            chart: {
              type: "bar",
              toolbar: { show: false },
              fontFamily: "inherit",
              foreColor: "#64748b",
            },
            plotOptions: {
              bar: {
                horizontal: true,
                borderRadius: 6,
                distributed: true,
                barHeight: "72%",
              },
            },
            colors: modelRows.map(
              (_, i) => RP_PRODUCTION_TEALS[i % RP_PRODUCTION_TEALS.length],
            ),
            fill: { opacity: 1 },
            xaxis: {
              categories: modelRows.map((m) => m.name),
              labels: { style: { colors: "#64748b", fontSize: "12px" } },
              axisBorder: { show: false },
              axisTicks: { show: false },
            },
            yaxis: {
              labels: { style: { colors: "#334155", fontSize: "12px" } },
            },
            dataLabels: {
              enabled: true,
              formatter: (v) => `${v.toLocaleString("tr-TR")} ad.`,
              style: { colors: ["#fff"], fontSize: "11px", fontWeight: 600 },
            },
            legend: { show: false },
            grid: {
              borderColor: "#e2e8f0",
              strokeDashArray: 4,
              xaxis: { lines: { show: true } },
              yaxis: { lines: { show: false } },
            },
            tooltip: {
              theme: "light",
              y: { formatter: (v) => `${v.toLocaleString("tr-TR")} adet` },
            },
          }}
          height={Math.max(280, modelRows.length * 48)}
        />
      </CardContainer>
    );
  };

  return (
    <DeliveriesReportShell
      title="Model Bazlı Teslimat Raporu"
      year={year}
      years={years}
      onYearChange={handleYearChange}
      showMonthFilter
      month={month}
      monthOptions={monthOptions}
      onMonthChange={setMonth}
      toasts={toasts}
      dismissToast={dismissToast}
    >
      {content()}
    </DeliveriesReportShell>
  );
};

export default withAuth(DeliveriesByModelReport);
